import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { Mail, MessageSquare, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extracted function for tracking page visits
const trackPageVisit = async () => {
  try {
    await axios.post(`${API}/analytics/visit`, {
      page: window.location.pathname,
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    // Error tracking would go here (e.g., Sentry)
  }
};

// Extracted function for form submission
const submitContactForm = async (formData) => {
  const response = await axios.post(`${API}/contact`, formData);
  return response;
};

// Form field component
const FormField = ({ id, name, type, label, icon: Icon, value, onChange, required, as: Component = Input, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-base font-medium flex items-center gap-2 text-gray-300">
      <Icon className="h-4 w-4 text-blue-500" />
      {label}
    </Label>
    <Component
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className={`text-base bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500 ${Component === Textarea ? 'resize-none' : 'h-12'}`}
      {...props}
    />
  </div>
);

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    trackPageVisit();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await submitContactForm(formData);
      
      if (response.status === 200) {
        toast({
          title: 'Success!',
          description: 'Your message has been sent successfully.',
        });
        
        setFormData({
          name: '',
          email: '',
          message: '',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send your message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 md:py-20 px-4 bg-black">
      <div className="container mx-auto max-w-2xl">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl shadow-2xl shadow-blue-500/20 border border-gray-800 overflow-hidden">
          <div className="text-center space-y-2 p-8 md:p-10 bg-gradient-to-br from-gray-800/50 to-black">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Get In Touch
            </h2>
            <p className="text-base md:text-lg text-gray-400">
              Have questions? We'd love to hear from you.
            </p>
          </div>
          <div className="p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormField
                id="name"
                name="name"
                type="text"
                label="Name"
                icon={User}
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <FormField
                id="email"
                name="email"
                type="email"
                label="Email"
                icon={Mail}
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <FormField
                id="message"
                name="message"
                label="Message"
                icon={MessageSquare}
                placeholder="Tell us what's on your mind..."
                value={formData.message}
                onChange={handleChange}
                required
                as={Textarea}
                rows={5}
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-base font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/50 hover:shadow-xl hover:shadow-blue-500/70"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
